import { useCallback, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Location } from 'react-router-dom';

type Listener = (location: Location) => void;

const listeners = new Set<Listener>();

function notifyListeners(location: Location) {
  listeners.forEach(listener => listener(location));
}

export function useHistory() {
  const navigate = useNavigate();
  const location = useLocation();
  const locationRef = useRef(location);

  useEffect(() => {
    if (location !== locationRef.current) {
      locationRef.current = location;
      notifyListeners(location);
    }
  }, [location]);

  const listen = useCallback((fn: Listener) => {
    listeners.add(fn);
    fn(locationRef.current);

    return () => {
      listeners.delete(fn);
    };
  }, []);

  const push = useCallback(
    (to: string | { pathname?: string; search?: string; state?: any }) => {
      if (typeof to === 'string') {
        navigate(to);
      } else {
        const path = to.pathname ?? location.pathname;
        let search = '';

        if (to.search) {
          search = to.search.startsWith('?') ? to.search : `?${to.search}`;
        }

        navigate(`${path}${search}`, { state: to.state });
      }
    },
    [navigate, location.pathname],
  );

  const replace = useCallback(
    (to: string | { pathname?: string; search?: string; state?: any }) => {
      if (typeof to === 'string') {
        navigate(to, { replace: true });
      } else {
        const path = to.pathname ?? location.pathname;
        let search = '';

        if (to.search) {
          search = to.search.startsWith('?') ? to.search : `?${to.search}`;
        }

        navigate(`${path}${search}`, { state: to.state, replace: true });
      }
    },
    [navigate, location.pathname],
  );

  return {
    push,
    replace,
    go: navigate,
    goBack: () => navigate(-1),
    goForward: () => navigate(1),
    listen,
    location,
  };
}
